import { Component, Input, OnDestroy, OnInit, Optional, TemplateRef, ViewChild, ViewChildDecorator } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LoggingService } from '../utils/logging.service';
import { FormFieldParams, IAdminFormFieldProvider } from './admin-form-field-provider';

/**
 * Diese Komponente wird benutzt um abhängig vom spezifizierten "type" eine spezielle
 * Inputkomponente ("AdminFormFieldProvider") anzuzeigen. So können Komponenten aus
 * anderen Microservices referenziert werden.
 *
 * Der übergebene "type" sowie andere Properties werden vom injecteten Proxyprovider
 * des Portal-Service an den korrekten Provider übergeben, der {@link IViewProvider}
 * implementiert.
 */
@Component({
    selector: 'common-admin-form-field',
    templateUrl: 'admin-form-field.component.html'
})
export class AdminFormFieldComponent implements OnInit, OnDestroy {
    /** Template für das Formular-Feld. */
    template: TemplateRef<FormFieldParams>;

    /** Der Typ des Formularfeldes (z.B. "roles" oder "portalLink") */
    @Input()
    type: string;

    /** group für das Formularfeld.*/
    @Input()
    group: UntypedFormGroup;

    /** formControlName für das Formularfeld.*/
    @Input()
    fcName: string;

    @Input()
    required: boolean;

    @Input()
    label: string;

    @Input()
    toolTip: string;

    /** Ob mehrere Werte eingegeben werden können sollen.*/
    @Input()
    multi: boolean;

    @Input()
    /** Additional parameters to pass them to the template (for example: {'rolePatterns': [ROLE_PATTERN_*, ...], ...}) */
    additionalParams?: { [key: string]: any };

    private subscription: Subscription;

    /**
     * Konstruktor.
     *
     * @param provider Grundsätzlich ein Proxy-Provider des Portals,
     *                 der den tatsächlichen Provider im Hintergrund instanziiert
     *                 und das Template daraus holt.
     * @param log Logging-Service
     */
    constructor(@Optional() private provider: IAdminFormFieldProvider,
                private log: LoggingService) {
        this.log.debug('injecteter provider:');
        this.log.debug(this.provider?.constructor?.name);
        this.log.debug(this.provider);
    }

    /**
     * Damit das zuverlässig funktioniert, sollten alle "templatebereitstellenden Komponenten", also
     * Implementierer von {@link IAdminFormFieldProvider} ihr Template in der Angular-Query
     * ({@link ViewChild}/{@link ViewChildDecorator}) mit dem Flag <code>{static: true}</code> aufrufen.
     * Siehe Kommentare in https://jira.t-systems-mms.eu/browse/SPP-1369.
     */
    ngOnInit(): void {
        if (this.provider && this.type) {
            this.subscription = this.provider.getFormFieldTemplate(this.type, this.multi).subscribe(template => {
                if (!template) {
                    this.log.warn('AdminFormFieldComponent#subscription: template is null!');
                }/* else {
                    this.log.debug('AdminFormFieldComponent#subscription: template is:');
                    this.log.debug(template);
                }*/
                this.template = template;
            });
        } else if (!this.provider) {
            this.log.debug('AdminFormFieldComponent#ngOnInit: this.provider ist null!');
        } else if (!this.type) {
            this.log.debug('AdminFormFieldComponent#ngOnInit: this.type ist null!');
        }
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    /** Transformiert eine Komma-separierte Aufzählung (String) in ein Array. */
    splitValuesToArray(commaSeparated: string): string[] | null {
        return commaSeparated?.split(',').map(v => v.trim()) || null;
    }

    /** Transformiert einen Array in eine Komma-separierte Aufzählung (String). */
    joinValuesFromArray(values: string[]) {
        return values?.join(',');
    }
}
